import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CirclePlay, Grip, Pencil, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'

import { ConfirmButton } from '@/components/ConfirmButton'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Skeleton } from '@/components/elements/skeleton'
import { Layout } from '@/components/Layout'
import { deleteFaq, getFaqs, reorderFaqs } from '@/services/faq.service'
import { toast } from '@/utils/helpers'

import AddEditFaqModal from './AddEditFaqModal'

const FaqRow = ({ faq, index, moveTo, onEdit, onDelete, deleting }) => {
  const [{ opacity }, drag] = useDrag(
    () => ({
      type: 'faq',
      item: { index },
      collect: (monitor) => ({ opacity: monitor.isDragging() ? 0.5 : 1 }),
    }),
    [index],
  )
  const [, drop] = useDrop(
    () => ({
      accept: 'faq',
      drop({ index: draggedIndex }) {
        if (draggedIndex !== index) moveTo(draggedIndex, index)
      },
    }),
    [index, moveTo],
  )

  return (
    <li
      ref={drop}
      className="flex items-center justify-between gap-4 px-4 py-3"
      style={{ opacity }}
    >
      <div className="flex min-w-0 flex-row items-center gap-3">
        <div ref={drag}>
          <Grip className="text-muted-foreground h-4 w-4 shrink-0 cursor-move" />
        </div>
        <span className="truncate text-sm font-medium">{faq.question}</span>
        {!faq.isVisible && (
          <Badge variant="secondary" className="shrink-0">
            Hidden
          </Badge>
        )}
        {(faq.videoUrl || faq.videoPath) && (
          <Badge variant="outline" className="shrink-0 gap-1">
            <CirclePlay className="h-3 w-3" />
            Video
          </Badge>
        )}
      </div>
      <div className="flex shrink-0 flex-row gap-2">
        <Button variant="outline" size="sm" onClick={() => onEdit(faq)}>
          <Pencil className="mr-1 h-4 w-4" />
          Edit
        </Button>
        <ConfirmButton
          confirmContent={`Are you sure you want to delete: ${faq.question}?`}
          confirmTitle="Delete FAQ"
          variant="destructive"
          size="sm"
          loading={deleting}
          onClick={() => onDelete(faq)}
        >
          <Trash2 className="h-4 w-4" />
          <span className="sr-only">{`Delete ${faq.question}`}</span>
        </ConfirmButton>
      </div>
    </li>
  )
}

FaqRow.propTypes = {
  faq: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  moveTo: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  deleting: PropTypes.bool,
}

/**
 * RescueX admin FAQ management (RSCX-353). FAQs are global platform guidance;
 * accounts see published entries under their FAQs nav item. Order is managed
 * by dragging rows — positions are persisted server-side.
 */
const Faqs = observer(() => {
  const [showModal, setShowModal] = useState(false)
  const [editFaq, setEditFaq] = useState(null)
  // Local mirror of the query data so a drag reorders instantly; the reorder
  // mutation then persists and the queries re-sync.
  const [items, setItems] = useState([])

  const queryClient = useQueryClient()

  const { data: faqs, isPending } = useQuery({
    queryKey: ['faqs', 'admin'],
    queryFn: () => getFaqs({ includeHidden: true }),
  })

  useEffect(() => {
    if (faqs) setItems(faqs.results ?? faqs ?? [])
  }, [faqs])

  const invalidateFaqs = () => queryClient.invalidateQueries({ queryKey: ['faqs'] })

  const { isPending: pendingDelete, mutate: removeFaq } = useMutation({
    mutationFn: (faq) => deleteFaq(faq.id),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast('FAQ deleted', 'success')
      invalidateFaqs()
    },
  })

  const { mutate: saveOrder } = useMutation({
    mutationFn: (ids) => reorderFaqs(ids),
    onError: (err) => {
      toast(err.message, 'error')
      // Re-sync the local list with the server's order.
      invalidateFaqs()
    },
    onSuccess: invalidateFaqs,
  })

  const moveTo = (fromIndex, toIndex) => {
    const next = [...items]
    const [moved] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, moved)
    setItems(next)
    saveOrder(next.map((faq) => faq.id))
  }

  return (
    <Layout breadcrumbs={['Admin', 'FAQs']}>
      <div className="flex flex-row items-center justify-between">
        <Layout.Heading title="FAQs" />
        <Button onClick={() => setShowModal(true)}>Add FAQ</Button>
      </div>
      <p className="text-muted-foreground mt-1 text-sm">
        Platform guidance shown to every account under its FAQs nav item. Drag rows to change the
        display order; hidden entries stay invisible to users until published.
      </p>

      {(() => {
        if (isPending) return <Skeleton className="mt-4 h-32 w-full" />
        if (items.length === 0) {
          return (
            <p className="text-muted-foreground mt-8 text-center text-sm">
              No FAQs yet. Add the first one!
            </p>
          )
        }
        return (
          <ul className="mt-4 divide-y rounded border">
            {items.map((faq, index) => (
              <FaqRow
                key={faq.id}
                faq={faq}
                index={index}
                moveTo={moveTo}
                deleting={pendingDelete}
                onDelete={removeFaq}
                onEdit={(selected) => {
                  setEditFaq(selected)
                  setShowModal(true)
                }}
              />
            ))}
          </ul>
        )
      })()}

      {showModal && (
        <AddEditFaqModal
          editFaq={editFaq}
          key={editFaq?.id ?? 'new'}
          resetAndClose={() => {
            setShowModal(false)
            setEditFaq(null)
            invalidateFaqs()
          }}
        />
      )}
    </Layout>
  )
})

export default Faqs
